import { format, parseISO } from "date-fns"
import {
  PromotionBumpChartPoint,
  PromotionEffectivenessPeriod,
  PromotionPeerEffectiveness,
} from "../../../../../api/promotion_effectiveness"

export interface BumpChartRow {
  period: string
  [promotionName: string]: string | number | null
}

export function compact_number(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "N/A"
  return new Intl.NumberFormat("en-US", {
    notation: Math.abs(value) >= 1000000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 1,
  }).format(value)
}

export function percent_value(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "N/A"
  return `${(value * 100).toFixed(1)}%`
}

export function money_value(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "N/A"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value)
}

export function cases_value(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "N/A"
  return `${compact_number(value)} cs`
}

/** Shows a dollar amount when present, otherwise the case-volume equivalent
 * (e.g. Anaplan plan metrics carry units but no sales dollars). */
export function amount_or_cases(dollars: number | null | undefined, units: number | null | undefined): string {
  if (dollars != null && !Number.isNaN(dollars)) return money_value(dollars)
  if (units != null && !Number.isNaN(units)) return cases_value(units)
  return "N/A"
}

export function period_label(period_start: string): string {
  return format(parseISO(period_start), "MMM yyyy")
}

export function build_period_chart_rows(periods: PromotionEffectivenessPeriod[]) {
  return periods.map((period: PromotionEffectivenessPeriod) => ({
    period: period_label(period.period_start),
    planned_spend: period.planned_spend,
    actual_spend: period.actual_spend,
    baseline_sales: period.baseline_sales ?? 0,
    incremental_sales: period.incremental_sales ?? 0,
    baseline_units: period.baseline_units ?? 0,
    incremental_units: period.incremental_units ?? 0,
    lift_pct: period.lift_pct ?? null,
    roi: period.roi ?? null,
    support_label: period.support_label,
  }))
}

/** True when periods can render a case-volume split even though dollar sales are absent. */
export function has_case_split(periods: PromotionEffectivenessPeriod[]): boolean {
  return periods.some(
    (period: PromotionEffectivenessPeriod) => period.baseline_units != null && period.incremental_units != null
  )
}

export function build_bump_chart_rows(points: PromotionBumpChartPoint[]): BumpChartRow[] {
  const rows_by_period: Record<string, BumpChartRow> = {}
  points.forEach((point: PromotionBumpChartPoint) => {
    const label = period_label(point.period)
    rows_by_period[label] = rows_by_period[label] ?? { period: label }
    rows_by_period[label][point.promotion_name] = point.rank
  })
  return Object.values(rows_by_period)
}

export function bump_chart_promotions(points: PromotionBumpChartPoint[]): string[] {
  return Array.from(new Set(points.map((point: PromotionBumpChartPoint) => point.promotion_name))).slice(0, 8)
}

export function build_peer_scatter_rows(peers: PromotionPeerEffectiveness[]) {
  return peers.map((peer: PromotionPeerEffectiveness) => ({
    name: peer.name,
    actual_spend: peer.actual_spend,
    incremental_sales: peer.incremental_sales ?? 0,
    roi: peer.roi ?? null,
    promotion_type: peer.promotion_type ?? "Unknown",
  }))
}
